import { profileHeaderStyles, BLUE, PURPLE } from "../styles/shared/profileHeaderStyles";
// src/components/TabsHeader.jsx
import React from "react";
import {
  Alert,
  Box,
  Fade,
  MenuItem,
  Popper,
  Select,
  Typography } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import TabNavigationBar from "./TabNavigationBar";

export default function ProfileHeader({
  title = "Company Profile",
  tab,
  onTabChange,
  tabs = [],
  counts = {},
  summaryRow = null,
  metaLabel,
  metaValue,
  onMetaValueClick,
  dateText,
  showAction = true,
  actionLabel = "",
  actionValue,
  onActionChange,
  actionPlaceholder = "New",
  actionPlaceholderValue = "",
  actionOptions = [],
  actionDisabled = false,
  actionSx = {},
  actionShowPlaceholderOption = true,
  actionFeedback = null,
  onActionFeedbackClose,
  sx = {} }) {
  const safeTabs = Array.isArray(tabs) ? tabs : [];
  const safeActionOptions = Array.isArray(actionOptions) ? actionOptions : [];
  const includePlaceholderActionOption =
    actionShowPlaceholderOption &&
    !safeActionOptions.some(
      (option) =>
        String(option?.value ?? "") === String(actionPlaceholderValue ?? "") ||
        String(option?.label || "").trim() === String(actionPlaceholder || "").trim()
    );
  const actionAnchorRef = React.useRef(null);
  const actionFeedbackOpen = Boolean(actionFeedback?.open && actionFeedback?.message);

  React.useEffect(() => {
    if (!actionFeedbackOpen || typeof onActionFeedbackClose !== "function") return undefined;

    const timeout = window.setTimeout(() => {
      onActionFeedbackClose();
    }, Number(actionFeedback?.autoHideDuration || 2500));

    return () => window.clearTimeout(timeout);
  }, [actionFeedback?.autoHideDuration, actionFeedbackOpen, onActionFeedbackClose]);

  return (
    <Box
      sx={profileHeaderStyles.root({ sx })}
    >
      <Box
        sx={profileHeaderStyles.titleRow()}
      >
        <Typography
          className="profile-header-title"
          sx={profileHeaderStyles.title()}
        >
          {title}
        </Typography>

        <Box
          sx={profileHeaderStyles.metadata()}
        >
          {metaLabel ? (
            <Typography
              className="profile-header-meta-label"
              sx={profileHeaderStyles.metadataLabel()}
            >
              {metaLabel}:
            </Typography>
          ) : null}

          {metaValue ? (
            <Typography
              className="profile-header-meta-value"
              component={onMetaValueClick ? "button" : "span"}
              onClick={onMetaValueClick}
              sx={profileHeaderStyles.metadataValue({ onMetaValueClick })}
            >
              {metaValue}
            </Typography>
          ) : null}

          {dateText ? (
            <>
              <Typography
                className="profile-header-date-divider"
                sx={profileHeaderStyles.dateDivider()}
              >
                |
              </Typography>
              <Typography
                className="profile-header-date-text"
                sx={profileHeaderStyles.dateText()}
              >
                {dateText}
              </Typography>
            </>
          ) : null}
        </Box>
      </Box>

      <Box
        className="profile-header-tab-row"
        sx={profileHeaderStyles.tabRow()}
      >
        <TabNavigationBar
          wrapperSx={profileHeaderStyles.tabWrapper()}
          tabsClassName="profile-header-tabs"
          value={tab}
          onChange={(value) => onTabChange?.(value)}
          items={safeTabs.map((item) => {
            const rawCount =
              item?.count !== undefined
                ? item.count
                : item?.countKey && counts && typeof counts === "object"
                  ? counts[item.countKey]
                  : undefined;
            const normalizedCount =
              rawCount === undefined || rawCount === null || rawCount === ""
                ? undefined
                : Number(rawCount);

            return {
              value: item.value,
              label: item.label,
              count: Number.isFinite(normalizedCount) ? normalizedCount : undefined };
          })}
          borderBottom={false}
          contentSizedIndicator={false}
          contentSizedIndicatorHeight={2}
          indicatorHeight={2}
          activeColor={BLUE}
          idleColor={PURPLE}
          labelSx={profileHeaderStyles.tabLabel()}
          countSx={profileHeaderStyles.tabCount()}
          tabGap={3}
          tabsSx={profileHeaderStyles.tabs()}
        />

        {showAction ? (
          <Box
            sx={profileHeaderStyles.actionWrapper()}
          >
            {actionLabel ? (
              <Typography
                className="profile-header-action-label"
                sx={profileHeaderStyles.actionLabel()}
              >
                {actionLabel}:
              </Typography>
            ) : null}

            <Box ref={actionAnchorRef} sx={profileHeaderStyles.actionAnchor()}>
              <Select
                className="profile-header-action-select"
                size="small"
                displayEmpty
                value={actionValue ?? ""}
                onChange={(event) => onActionChange?.(event.target.value)}
                disabled={actionDisabled}
                IconComponent={KeyboardArrowDownRoundedIcon}
                sx={profileHeaderStyles.actionSelect({ actionSx })}
                MenuProps={{
                  PaperProps: {
                    sx: profileHeaderStyles.actionMenu({ actionAnchorRef }) } }}
                renderValue={(value) => {
                  const matchedOption = safeActionOptions.find(
                    (option) => String(option?.value ?? "") === String(value ?? "")
                  );

                  if (
                    !matchedOption &&
                    (value === undefined ||
                      value === null ||
                      String(value) === "" ||
                      String(value) === String(actionPlaceholderValue ?? ""))
                  ) {
                    return (
                      <Box component="span" sx={profileHeaderStyles.placeholder()}>
                        {actionPlaceholder}
                      </Box>
                    );
                  }

                  return matchedOption?.label || String(value);
                }}
              >
                {includePlaceholderActionOption ? (
                  <MenuItem
                    value={actionPlaceholderValue ?? ""}
                    sx={profileHeaderStyles.placeholderOption()}
                  >
                    {actionPlaceholder}
                  </MenuItem>
                ) : null}

                {safeActionOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value} sx={profileHeaderStyles.menuOption({ option })}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Popper
              open={Boolean(actionFeedbackOpen && actionAnchorRef.current)}
              anchorEl={actionAnchorRef.current}
              placement="top"
              transition
              modifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [0, 10] } },
              ]}
              sx={profileHeaderStyles.feedbackPopper()}
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={180}>
                  <Alert
                    severity={actionFeedback?.severity || "success"}
                    onClose={() => onActionFeedbackClose?.()}
                    sx={profileHeaderStyles.feedbackAlert()}
                  >
                    {actionFeedback?.message || ""}
                  </Alert>
                </Fade>
              )}
            </Popper>
          </Box>
        ) : (
          <Box sx={profileHeaderStyles.emptyAction()} />
        )}
      </Box>

      {summaryRow ? <Box sx={profileHeaderStyles.summary()}>{summaryRow}</Box> : null}
    </Box>
  );
}
